import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf } from '../skillUsage';

export const softFocusResolveSkill: CodeoSkill = {
    name: SKILL_NAMES.softFocusResolve,
    kind: 'codeo',
    description:
        'Bring an element in out of blur, as though a lens were racking onto it — for a headline, ' +
        'a hero figure, or handing attention from one layer of the frame to another.',
    documentUsesSkill: check((script) => /filter/.test(script) && countOf(script, /blur\(/g) >= 2),
    instructions: `# Soft Focus Resolve

An element arrives already in place but out of focus, and the blur resolves. Because real cameras behave this way, it reads as *attention landing* rather than as an object moving — which makes it the cleanest way to say "look here" without sliding anything.

It also does something no other entrance does: it can hand focus *between* two elements, pulling one out of focus as the other comes in. That is a phase change carried entirely by the lens.

## Markup

\`\`\`html
<div id="hero-line" style="position: absolute; left: 180px; top: 420px; width: 1180px; font-family: var(--l5-font-0), sans-serif; font-size: 116px; font-weight: 800; line-height: 1.02; color: var(--l5-color-0, #101820);">Everything, in one pass</div>
<div id="support-line" style="position: absolute; left: 180px; top: 700px; width: 900px; font-family: var(--l5-font-0), sans-serif; font-size: 40px; font-weight: 500; color: var(--l5-color-1, #5b6472);">No exports. No re-cuts.</div>
\`\`\`

## Timeline code

\`filter\` is not a transform channel, so blur composes with anything else on the element.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#hero-line', { filter: 'blur(22px)', opacity: 0 });
gsap.set('#support-line', { filter: 'blur(14px)', opacity: 0 });

tl.to('#hero-line', {
    filter: 'blur(0px)',
    opacity: 1,
    duration: 0.9,
    ease: 'power3.out',
}, 0.2);

// The rack: the hero softens slightly as the support line takes focus.
tl.to('#hero-line', { filter: 'blur(5px)', opacity: 0.55, duration: 0.7, ease: 'power2.inOut' }, 2.4);
tl.to('#support-line', { filter: 'blur(0px)', opacity: 1, duration: 0.7, ease: 'power2.inOut' }, 2.45);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Blur depth**: 18–26px for a hero at large sizes; 10–14px for body copy. Too little reads as a rendering artifact rather than a choice.
- **Focus-in only**: drop the rack and keep the first tween — the quiet version, good under narration that is still setting up.
- **Rack between layers**: blur the *background* layer up as foreground content lands, which separates them without moving either.
- **With a scale**: a 1.04 → 1.0 scale alongside the blur reads as a lens breathing. Keep it under 1.06 or it becomes a zoom.
- **Blur out**: exiting into blur rather than into nothing hands the frame on rather than emptying it.
- **Pair with a push**: a slow camera push during the resolve compounds the sense of the frame settling — see \`${SKILL_NAMES.cinematicCameraMove}\`.

## Pitfalls

- Blur is expensive to rasterize over a large area. Blur the text element, not a full-frame wrapper containing everything.
- Tween the whole \`filter\` string (\`'blur(22px)'\` → \`'blur(0px)'\`), not a bare number — GSAP needs the unit to interpolate the function.
- If the element already carries other filters, include them in *both* the from and to strings or they are dropped mid-tween.
- Don't blur something the viewer needs to read while narration is naming it. The resolve must land on the word, not after it.
- Blur plus a slide plus a fade is three entrances stacked. Pick the blur and let it carry the arrival.
- A blurred element still occupies its box; blur does not shrink it. Leave room, or the soft edge will clip against a neighbour.
- \`gsap.set\` establishes the from-states at build time, so no tween here needs \`immediateRender: false\`.`,
};
